import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TextField, Button, CardMedia, Input } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useFormik } from "formik";
import * as Yup from "yup";

import { UploadMedia } from "../../utils/mediaUpload";
import { showToast, showError } from "../../constants/toasts";
import { usePutUpdateProfileMutation, useGetUserQuery } from "../../services/auth";
import { getFromStorage } from "../../helpers/storage";
import { STORAGE_KEYS } from "../../constants/storageKeys";

const EditProfile = () => {
  const navigate = useNavigate();
  const [updateProfile] = usePutUpdateProfileMutation();
  
     // Get user data from API
   const { data: userData, isLoading, refetch } = useGetUserQuery({});
   const user = userData?.data;

  const [profileImage, setProfileImage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageId, setImageId] = useState<number | null>(null);

  // Update profile image when user data loads
  React.useEffect(() => {
    if (user?.profile_picture?.media_file_url) {
      setProfileImage(user.profile_picture.media_file_url);
    }
  }, [user]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: user?.first_name || "",
      email: user?.email || "",
      phone_number: "1234567890", // Static phone number
      countryCode: "971", // Static country code for UAE
    },
    validationSchema: Yup.object({
      fullName: Yup.string()
        .min(2, "Full name must be at least 2 characters")
        .max(50, "Full name too long")
        .required("Full name is required"),

      phone_number: Yup.string()
        .matches(/^[0-9]{7,15}$/, "Enter a valid phone number")
        .required("Phone number is required"),
      countryCode: Yup.string().required("Country code is required"),
    }),
    onSubmit: async (values) => {
      const body: any = {
        first_name: values.fullName || "",
        phone_number: values.phone_number || "",
        country_code: `+${values.countryCode}` || "",
      };
   
      
      // Handle profile picture: send new image ID if uploaded, otherwise keep existing
      if (imageId) {
        body.profile_picture = imageId;
      } else if (user?.profile_picture?.id) {
        body.profile_picture = user.profile_picture.id;
      }
             try {
         const response = await (updateProfile as any)(body).unwrap();
         if ((response as any)?.status === 200) {
           showToast("Profile updated successfully");    
           await refetch()
             navigate("/dashboard");
         }
       } catch (error) {
         console.error("Profile update failed:", error);
         showError("Failed to update profile");
       }
    },
  });

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfileImage("");
      setImageId(null);
      return;
    }

    const response = (await UploadMedia(file)) as any;
    if (
      response?.data &&
      Array.isArray(response.data) &&
      response.data.length > 0
    ) {
      const uploadedImage = response.data[0];
      if (uploadedImage.media_file_url && uploadedImage.id) {
        setProfileImage(uploadedImage.media_file_url);
        setImageId(uploadedImage.id);
      }
    }
  };

  // Show loading state while fetching user data
  if (isLoading) {
    return (
      <div className="main_title">
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <>
      <div className="main_title">
        <h1>Edit Profile</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/manage-profile")}>
            Manage Profile
          </span>{" "}
          - Edit Profile
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_100">
              <label>Upload Image</label>
              {profileImage ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={profileImage}
                        alt="photo"
                      />
                    </figure>
                                         <CloseIcon
                       onClick={() => {
                         // If this is a newly uploaded image, clear it
                         if (imageId) {
                           setProfileImage("");
                           setSelectedFile(null);
                           setImageId(null);
                         } else {
                           // If this is the existing profile picture, just clear the display
                           setProfileImage("");
                         }
                       }}
                     />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg",
                    }}
                    onChange={(e: any) => {
                      handleProfileUpload(e);
                      e.target.value = "";
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/user_placeholder.png"
                        alt="icon"
                      />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>
            <div className="control_group w_33">
              <label>Name</label>
              <TextField
                name="fullName"
                placeholder="Enter Name"
                hiddenLabel
                fullWidth
                value={formik.values.fullName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.fullName && Boolean(formik.errors.fullName)
                }
                helperText={formik.touched.fullName && formik.errors.fullName as string}
              />
            </div>
            <div className="control_group w_33">
              <label>Email Id</label>
              <TextField
                name="email"
                placeholder="Enter Email Id"
                hiddenLabel
                fullWidth
                disabled
                value={formik.values.email}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email as any}
              />
            </div>
            <div className="control_group w_33">
              <label>Phone Number</label>
                             <PhoneInput
                 enableSearch={false}
                 disabled={true}
                 value={formik.values.countryCode + formik.values.phone_number}
                 country={"ae"}
                 inputClass="phoneInput"
                 onChange={(value, country: any) => {
                   const dialCode = country?.dialCode;
                   const phoneNumber = value.replace(dialCode, "");
                   formik.setFieldValue("phone_number", phoneNumber);
                   formik.setFieldValue("countryCode", dialCode);
                 }}
                 onBlur={() => formik.setFieldTouched("phone_number", true)}
                 inputStyle={{ width: "100%" }}
                 buttonClass="phoneBtn"
                 placeholder="Phone Number"
               />
              {(formik.touched.phone_number && formik.errors.phone_number) ||
              formik.errors.countryCode ? (
                <div className="error_text">
                  {(formik.errors as any).phone ||
                    (formik.errors as any).countryCode}
                </div>
              ) : null}
            </div>
          </div>
          <div className="form_btn">
            <Button className="btn btn_primary" type="submit">
              Submit
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditProfile;
